'use client';

import { useEffect, useMemo } from 'react';
import { useParams } from 'next/navigation';
import { useTranslations } from 'use-intl';
import ProductDetails from '@/components/organisms/product-details';
import { useStoreAndBusinessUnits } from '@/providers/store-and-business-units';
import useAccount from '@/lib/hooks/useAccount';
import useAccountRoles from '@/lib/hooks/useAccountRoles';
import useShoppingListAccess from '@/lib/hooks/useShoppingListAccess';
import { getPriceAvailability } from '@/hooks/usePriceDisplay';
import { Product as SharedProduct } from '@shared/types/product';
import { pushViewItem } from '@/lib/analytics/dataLayer';
import { extractElectusCategory } from '@/lib/analytics/ga4Items';
import { getDeliverableEmail } from '@/utils/get-deliverable-email';
import { getProductsIndexName } from '@/lib/algolia';
import { barcodeSpecificationRows } from '@/utils/product/barcode-specifications';
import { ProductDetailsMapperProps } from '../types';
import usePDPWishlists from '../hooks/usePDPWishlists';
import usePDPCart from '../hooks/usePDPCart';
import usePDPProduct from '../hooks/usePDPProduct';
import useSupersededBy from '../hooks/useSupersededBy';
import usePdpInsights from '../hooks/usePdpInsights';
import type { CtProductsBySku } from '@/utils/server/getCtSnapshotForSku';
import type { Locale } from '@/project.config';
import type { Attribute } from '@/types/entity/product';

// EWM-192 — render the inline NotFound state when product is undefined
// (no /404 redirect). When the product is present, the server tastic
// has already awaited CT enrichment and passes resolved `ctData` —
// variants are enriched with channel-scoped price + stock + bulk-pricing
// tiers from CT in a single render.
const ProductDetailsMapper = ({ product, ctData }: ProductDetailsMapperProps) => {
  if (!product) return <ProductDetails state="not-found" />;
  return <ProductDetailsReady product={product} ctData={ctData ?? {}} />;
};

// Replaces every variant's `price` / `discountedPrice` / `isOnStock` /
// `priceBreaks` with the values resolved per-SKU by the server tastic
// (`getCtSnapshotForSku`). Standalone Prices on the dealer's channel
// are the only authoritative price source — Frontastic-embedded prices
// (which come from CT's heavy `query()` path and may be the public
// RRP) are intentionally stripped when CT has no snapshot for a SKU.
// The downstream UI shows "Unavailable" for those variants rather
// than the wrong price.
//
// EWM-598 — `discountedPrice` now flows from the authoritative CT snapshot
// (`snapshot.discountedPrice`, the product-discounted "now" price on the
// winning channel; tier = channel). It is mapped into the SharedProduct's
// `ProductDiscountedPrice` shape (`{ value: Money }`) so `map-product`'s
// existing `variant.discountedPrice?.value` reader surfaces it as the entity
// `Product.discountedPrice` — driving the PDP pricing card's was/now display.
// Only the snapshot value is trusted: the previous unconditional `undefined`
// existed to stop the embedded `query()`-path RRP from leaking in as a fake
// discount. The snapshot has no such ambiguity (it carries the real product
// discount, omitted when none applies), so we map it when present and keep it
// undefined otherwise.
//
// Same treatment is applied to `familyVariants` (cross-product family
// siblings) so the variant dropdown's sibling entries surface the same
// snapshot-sourced discount. The server tastic enriches variant + family
// SKUs through the same per-SKU helper, so the same cache entries
// serve both — and concurrent cache misses batch into one BFF call.
const enrichProductWithCtData = (product: SharedProduct, ctData: CtProductsBySku): SharedProduct => {
  return {
    ...product,
    variants: product.variants.map((variant) => {
      const snapshot = variant.sku ? ctData[variant.sku] : undefined;
      return {
        ...variant,
        price: snapshot?.activePrice,
        discountedPrice: snapshot?.discountedPrice ? { value: snapshot.discountedPrice } : undefined,
        // EWM-431 — RRP from the authoritative CT snapshot (price-rrp channel),
        // falling back to the variant's custom-attribute RRP when the snapshot
        // has none (the documented EWM-195/210 precedence; the attribute is
        // unused today so the fallback is normally a no-op). The pricing card
        // shows the RRP pill only when rrp > price.
        rrp: snapshot?.rrp ?? variant.rrp,
        isOnStock: typeof snapshot?.inStock === 'boolean' ? snapshot.inStock : undefined,
        priceBreaks: snapshot?.priceBreaks,
      };
    }),
    ...(product.familyVariants
      ? {
          familyVariants: product.familyVariants.map((fv) => {
            const snapshot = fv.sku ? ctData[fv.sku] : undefined;
            return {
              ...fv,
              price: snapshot?.activePrice,
              discountedPrice: snapshot?.discountedPrice ? { value: snapshot.discountedPrice } : undefined,
              isOnStock: typeof snapshot?.inStock === 'boolean' ? snapshot.inStock : undefined,
            };
          }),
        }
      : {}),
  };
};

const ProductDetailsReady = ({ product, ctData }: { product: SharedProduct; ctData: CtProductsBySku }) => {
  const enrichedProduct = enrichProductWithCtData(product, ctData);
  const { mappedProduct, onChangeSKU } = usePDPProduct(enrichedProduct);

  // EWM-809 — Algolia Insights: fire a viewedObjectIDs event once per SKU and
  // expose the originating queryID so the add-to-cart convert event (Task 5)
  // can be search-attributed.
  const { locale } = useParams<{ locale: Locale }>();
  const algoliaIndex = getProductsIndexName(locale);
  const { getSearchAttribution } = usePdpInsights(mappedProduct.sku ?? '', algoliaIndex);
  const { account } = useAccount();
  const translate = useTranslations();
  const { selectedBusinessUnit } = useStoreAndBusinessUnits();

  const { permissions } = useAccountRoles(selectedBusinessUnit?.key);
  const { canUsePurchaseLists } = useShoppingListAccess(selectedBusinessUnit?.key);

  const { addToWishlists, removeFromWishlists, addToNewWishlist, getWishlists } = usePDPWishlists(mappedProduct);

  // EWM-809 fix-wave D-1 — memoize the bridge so usePDPCart's addToCart callback
  // doesn't re-create on every render (getSearchAttribution is already stable via
  // useCallback; algoliaIndex is a derived string that only changes with locale).
  const insightsBridge = useMemo(() => ({ getSearchAttribution, algoliaIndex }), [getSearchAttribution, algoliaIndex]);
  const { addToCart } = usePDPCart(mappedProduct, insightsBridge);

  // EWM-195 — guest path (no account) shows RRP only + login CTA.
  const isGuest = !account;

  // EWM-936 — barcodes join the specifications table for signed-in customers
  // only; guests see the table exactly as before. Row composition lives in the
  // shared helper so it can be tested without standing up the whole PDP.
  const specifications = useMemo<Attribute[]>(() => {
    const base = mappedProduct.specifications ?? [];
    if (isGuest) return base;
    return [...base, ...barcodeSpecificationRows(mappedProduct.barcodes, translate)];
  }, [mappedProduct.specifications, mappedProduct.barcodes, isGuest, translate]);

  // EWM-197 — view-only role: authenticated but lacks the cart-write
  // permission. Distinct from `addToCartDisabled` which is the legacy
  // disabled-state flag (out-of-stock / no price). View-only HIDES the
  // stepper + Add per AC.
  const isViewOnly = !!account && !permissions.UpdateMyCarts;

  // Strict CT-as-authoritative gating, mirroring the PLP tile:
  //   - `priceUnavailable` — CT has no Standalone Price on the dealer's
  //     channel for the active SKU. The pricing card renders the
  //     "Unavailable" label; the cart CTA is disabled.
  //   - `stockUnavailable` — CT supplied no stock signal at all
  //     (`inStock` is undefined). Treated as out-of-stock; we never
  //     fall back to Algolia or embedded variant availability.
  const activeSku = mappedProduct.sku;
  const ctSnapshot = activeSku ? ctData[activeSku] : undefined;
  // EWM-525 — single source for the decision (shared with the PLP tile via
  // getPriceAvailability), so PLP and PDP can't drift.
  const { priceUnavailable, stockUnavailable } = getPriceAvailability(ctSnapshot);
  const addToCartDisabled = !permissions.UpdateMyCarts || priceUnavailable || stockUnavailable;

  const activePrice = ctSnapshot?.activePrice;
  const analyticsItemPrice = activePrice
    ? (activePrice.centAmount ?? 0) / Math.pow(10, activePrice.fractionDigits ?? 2)
    : 0;
  const analyticsItemCurrency = activePrice?.currencyCode === 'NZD' ? 'NZD' : 'AUD';

  useEffect(() => {
    if (!activeSku) return;
    const snapshot = activeSku ? ctData[activeSku] : undefined;
    const centAmount = snapshot?.activePrice?.centAmount ?? 0;
    const fractionDigits = snapshot?.activePrice?.fractionDigits ?? 2;
    const price = centAmount / Math.pow(10, fractionDigits);
    const currency = (snapshot?.activePrice?.currencyCode ?? 'AUD') as 'AUD' | 'NZD';

    pushViewItem({
      currency,
      item: {
        item_id: activeSku ?? '',
        item_name: mappedProduct.name ?? '',
        item_brand: mappedProduct.brand,
        item_category: extractElectusCategory(mappedProduct.categories ?? []),
        price,
        in_stock: snapshot?.inStock ?? false,
      },
    });
    // activeSku and ctData intentionally omitted: both are already current when
    // mappedProduct.sku changes (same render), and including ctData would cause
    // double-fires during the CT enrichment stream.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mappedProduct.sku]);

  // EWM-196 / EWM-214 — pre-fill values for the Request a Quote drawer.
  // Phone is now sourced from the Account: EWM-178 added customer-level
  // `phone` / `mobile` custom fields (read by the BFF AccountMapper from
  // `custom.fields`), so no address-level lookup is needed. Prefer the
  // landline `phone`, fall back to `mobile`; undefined for accounts with
  // neither (the drawer keeps its placeholder).
  const defaultName = account ? `${account.firstName ?? ''} ${account.lastName ?? ''}`.trim() || undefined : undefined;
  const defaultEmail = getDeliverableEmail(account); // EWM-663
  const defaultPhone = account?.phone ?? account?.mobile;

  // EWM-196 — Stub submit. Backend hasn't built `quote/submitProductQuoteRequest`
  // yet (per ticket); simulate a 600ms async success so the drawer's success
  // state renders. Swap-back when the BFF endpoint lands:
  //   1. Replace this stub with `await sdk.callAction({ actionName:
  //      'quote/submitProductQuoteRequest', payload })` (or whatever the BFF
  //      contract resolves to per W6).
  //   2. Map any backend error to a thrown rejection — RequestQuoteDrawer
  //      already surfaces that as the error state.
  //   3. Drop this comment block.
  const onSubmitQuote = () => new Promise<void>((resolve) => setTimeout(resolve, 600));

  // EWM-194 — resolve the raw CT `productReplacements[].id` array (mapped to
  // `supersededByRef`) into a `SupersededBy[]` list via a second BFF fetch.
  // No BE-side reference expansion is needed. Returns `undefined` when the
  // ref is unset, the lookup is in flight, or no referenced product
  // resolves — `MainInfo` and the discontinued state hide the alert in
  // those cases.
  const supersededBy = useSupersededBy(mappedProduct.supersededByRef?.ids);

  // EWM-192 — closes the deferred "discontinued detection" TBD. The product
  // mapper derives `isDiscontinued` from the variant lifecycle code; when
  // true, the page shell switches to the discontinued state (full-page
  // replacement). When the product is still browseable but has a successor,
  // `supersededBy` populates the in-panel "Superseded Product" alert
  // instead — see `MainInfo`.
  if (mappedProduct.isDiscontinued) {
    return (
      <ProductDetails
        state="discontinued"
        // EWM-192's discontinued page-shell renders a single replacement
        // card; pass the first resolved entry. Multi-replacement listing
        // for the discontinued state would be a follow-up.
        discontinuedDetails={supersededBy?.[0] ? { supersededBy: supersededBy[0] } : undefined}
      />
    );
  }

  // EWM-212 — downloads now wired to the real CT `variant.attributes.downloads`
  // attribute via `map-product.ts`. No fallback: products without downloads
  // hide the Downloads section per AC.
  // EWM-211 — related products are owned by the `related-products-slider`
  // Studio tastic (PR #167); no longer plumbed through this mapper.
  const downloads = mappedProduct.downloads;

  const readyProduct = supersededBy ? { ...mappedProduct, supersededBy } : mappedProduct;

  return (
    <ProductDetails
      state="ready"
      product={readyProduct}
      addToCartDisabled={addToCartDisabled}
      // EWM-360 — when the dealer's BU allows back-orders, an out-of-stock
      // product's Add to Cart stays enabled and relabels to "Back Order".
      backOrdersAllowed={selectedBusinessUnit?.backOrdersAllowed ?? false}
      priceUnavailable={priceUnavailable}
      stockUnavailable={stockUnavailable}
      canUsePurchaseLists={canUsePurchaseLists}
      canAddToOwnWishlist={permissions.UpdateMyShoppingLists}
      canAddToOthersWishlist={permissions.UpdateOthersShoppingLists}
      addToCart={addToCart}
      getWishlists={getWishlists}
      addToWishlists={addToWishlists}
      onChangeSKU={onChangeSKU}
      onSubmitQuote={onSubmitQuote}
      defaultName={defaultName}
      defaultEmail={defaultEmail}
      defaultPhone={defaultPhone}
      isGuest={isGuest}
      isViewOnly={isViewOnly}
      addToNewWishlist={addToNewWishlist}
      removeFromWishlists={removeFromWishlists}
      downloads={downloads}
      specifications={specifications}
      analyticsItem={{
        item_id: activeSku ?? '',
        item_name: mappedProduct.name ?? '',
        price: analyticsItemPrice,
        item_brand: mappedProduct.brand,
        item_category: extractElectusCategory(mappedProduct.categories ?? []),
        currency: analyticsItemCurrency,
      }}
    />
  );
};

export default ProductDetailsMapper;
